import React, { useEffect, useState } from 'react';
import { Modal, Radio, Space, Table, Row, Col, Select, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { fetchSqlDataWithQuery } from '../../services/api';
import type { WeldSelectValue } from './WeldSelectModal';
import { useGetQaqcPipeWeldingdataLog } from '../../hooks/genericHooks';
import type { QaqcPipeWeldingdataLog } from '../../types';

const inspTypes = ['BC','VC','PT','RT','MT','UT','PMI','PWHT'] as const;

type InspType = typeof inspTypes[number];

type PairRow = {
  key: string;
  weldingdata_id: string;
  weld_no_rev: number | string | null;
  line_no?: string;
  section?: string;
  fluid?: string;
  spool_id?: number | string;
  serviceclass_name?: string;
  pipesize_name?: string;
  weldtype_name?: string;
};

interface Props {
  open: boolean;
  onCancel: () => void;
  onOk: (items: WeldSelectValue[]) => void;
  defaultInspectionType?: InspType;
}

const PendingWeldsSelectModal: React.FC<Props> = ({ open, onCancel, onOk, defaultInspectionType }) => {
  const [inspectionType, setInspectionType] = useState<InspType>(defaultInspectionType || 'VC');
  const [pairs, setPairs] = useState<PairRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pageSize, setPageSize] = useState<number>(20);
  const [current, setCurrent] = useState<number>(1);

  // Weld log for building filter options
  const { data: logResp } = useGetQaqcPipeWeldingdataLog() as any;
  const weldingLogList: QaqcPipeWeldingdataLog[] = (logResp as any)?.data ?? (logResp as any) ?? [];

  const [filters, setFilters] = useState<{
    section?: string[];
    serviceclass_name?: string[];
    line_no?: string[];
    pipesize_name?: string[];
    weldtype_name?: string[];
    fluid?: string[];
    spool_id?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdataLog)[] = [
    'fluid',
    'section',
    'line_no',
    'spool_id',
    'serviceclass_name',
    'pipesize_name',
    'weldtype_name',
  ];

  const applyLogFilters = (
    dataIn: QaqcPipeWeldingdataLog[],
    opts?: { excludeField?: keyof QaqcPipeWeldingdataLog }
  ) => {
    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (opts?.excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '').toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cell.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).toLowerCase() === cell);
          if (!matched) return false;
        }
      }
      return true;
    });
  };

  const buildOptions = (field: keyof QaqcPipeWeldingdataLog) => {
    const base = applyLogFilters(weldingLogList, { excludeField: field });
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  useEffect(() => {
    if (!open) return;
    setInspectionType(defaultInspectionType || 'VC');
    setSelectedRowKeys([]);
    setFilters({});
  }, [open, defaultInspectionType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const t = String(inspectionType).replace(/'/g, "''");
      // pass fluid into the backend function so server-side filtering matches UI selections
      const tf = (Array.isArray(filters.fluid) && filters.fluid.length > 0)
        ? `'${String(filters.fluid[0]).replace(/'/g, "''")}'`
        : 'NULL';
      const pick = (arr?: string[]) => (Array.isArray(arr) && arr.length > 0 ? `'${String(arr[0]).replace(/'/g, "''")}'` : 'NULL');
      const q = `SELECT weldingdata_id, weld_no_rev, line_no, section, fluid
                 FROM public.qaqc_pipe_pending_inspections(
                   '${t}',
                   ${pick(filters.section)},
                   ${pick(filters.serviceclass_name)},
                   ${pick(filters.line_no)},
                   ${pick(filters.pipesize_name)},
                   ${pick(filters.weldtype_name)},
                   ${tf}
                 )`;
      console.log('[query_string_pending Welds]',{q})
      const resp = await fetchSqlDataWithQuery(q);
      const arr = Array.isArray(resp) ? resp : resp?.data ?? [];
      const safe = Array.isArray(arr) ? arr : [];
      // Build a quick lookup map by weldingdata_id from the log for extra fields used in cascading filters
      const infoMap = new Map<string, { spool_id?: number | string; serviceclass_name?: string; pipesize_name?: string; weldtype_name?: string }>();
      weldingLogList.forEach((row: any) => {
        const wid = row?.weldingdata_id != null ? String(row.weldingdata_id) : undefined;
        if (wid && !infoMap.has(wid)) {
          infoMap.set(wid, {
            spool_id: row.spool_id,
            serviceclass_name: row.serviceclass_name,
            pipesize_name: row.pipesize_name,
            weldtype_name: row.weldtype_name,
          });
        }
      });
      let mapped: PairRow[] = safe.map((r: any) => ({
        key: `${String(r.weldingdata_id)}|${String(r.weld_no_rev)}`,
        weldingdata_id: String(r.weldingdata_id),
        weld_no_rev: r.weld_no_rev ?? null,
        line_no: r.line_no,
        section: r.section,
        fluid: r.fluid,
        spool_id: infoMap.get(String(r.weldingdata_id))?.spool_id,
        serviceclass_name: infoMap.get(String(r.weldingdata_id))?.serviceclass_name,
        pipesize_name: infoMap.get(String(r.weldingdata_id))?.pipesize_name,
        weldtype_name: infoMap.get(String(r.weldingdata_id))?.weldtype_name,
      }));
      // Apply all selected filters client-side to ensure cascading across multiple selections
      const hasVals = (arr?: string[]) => Array.isArray(arr) && arr.length > 0;
      if (hasVals(filters.fluid)) {
        const set = new Set((filters.fluid as string[]).map(String));
        mapped = mapped.filter(row => row.fluid && set.has(String(row.fluid)));
      }
      if (hasVals(filters.spool_id)) {
        const set = new Set((filters.spool_id as string[]).map(String));
        mapped = mapped.filter(row => row.spool_id != null && set.has(String(row.spool_id)));
      }
      if (hasVals(filters.section)) {
        const set = new Set((filters.section as string[]).map(v => String(v).toLowerCase()));
        mapped = mapped.filter(row => row.section && set.has(String(row.section).toLowerCase()));
      }
      if (hasVals(filters.serviceclass_name)) {
        const set = new Set((filters.serviceclass_name as string[]).map(v => String(v).toLowerCase()));
        mapped = mapped.filter(row => row.serviceclass_name && set.has(String(row.serviceclass_name).toLowerCase()));
      }
      if (hasVals(filters.pipesize_name)) {
        const set = new Set((filters.pipesize_name as string[]).map(v => String(v).toLowerCase()));
        mapped = mapped.filter(row => row.pipesize_name && set.has(String(row.pipesize_name).toLowerCase()));
      }
      if (hasVals(filters.weldtype_name)) {
        const set = new Set((filters.weldtype_name as string[]).map(v => String(v).toLowerCase()));
        mapped = mapped.filter(row => row.weldtype_name && set.has(String(row.weldtype_name).toLowerCase()));
      }
      if (hasVals(filters.line_no)) {
        const vals = (filters.line_no as string[]).map(v => String(v).toLowerCase()).filter(v => v.trim() !== '');
        if (vals.length > 0) {
          mapped = mapped.filter(row => {
            const cell = String(row.line_no ?? '').toLowerCase();
            return vals.some(v => cell.includes(v));
          });
        }
      }
      setPairs(mapped);
    } catch {
      setPairs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inspectionType, filters]);

  const columns: ColumnsType<PairRow> = [
    { title: 'Welding Data ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 240 },
    { title: 'Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 80 },
    { title: 'Line No', dataIndex: 'line_no', key: 'line_no' },
    { title: 'Section', dataIndex: 'section', key: 'section', width: 80 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 80},
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
  ];

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <b>Select Pending Welds</b>
          <span style={{ marginLeft: 8, opacity: 0.8 }}>({inspectionType})</span>
        </div>
      }
      onCancel={onCancel}
      onOk={() => {
        const map = new Map<string, WeldSelectValue>();
        pairs.forEach(p => map.set(p.key, { weldingdata_id: p.weldingdata_id, weld_no_rev: p.weld_no_rev }));
        const items: WeldSelectValue[] = selectedRowKeys
          .map(k => map.get(String(k)))
          .filter(Boolean) as WeldSelectValue[];
        onOk(items);
      }}
      width={860}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Row gutter={[8,8]}>
          <Col xs={24}>
            <Radio.Group value={inspectionType} onChange={(e) => setInspectionType(e.target.value)}>
              {inspTypes.map(t => (<Radio.Button key={t} value={t}>{t}</Radio.Button>))}
            </Radio.Group>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('section')} placeholder="Area (Section)" value={filters.section}
                    onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('serviceclass_name')} placeholder="S.Class" value={filters.serviceclass_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('fluid')} placeholder="Fluid" value={filters.fluid}
                    onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('line_no')} placeholder="Line No" value={filters.line_no}
                    onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('pipesize_name')} placeholder="Pipe Size" value={filters.pipesize_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, pipesize_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={2}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('weldtype_name')} placeholder="Weld Type" value={filters.weldtype_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, weldtype_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('spool_id')} placeholder="Spool" value={filters.spool_id}
                    onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24}>
            <Space style={{ width: '100%' }}>
              <Button size="small" onClick={() => { setFilters({}); setCurrent(1); fetchData(); }}>Reset</Button>
              <span style={{ opacity: 0.75, marginLeft: 'auto' }}>Total: {pairs.length}</span>
            </Space>
          </Col>
        </Row>

        <Table<PairRow>
          size="small"
          rowKey={(r) => r.key}
          columns={columns}
          dataSource={pairs}
          loading={loading}
          pagination={{
            current,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10','20','50','100'],
            showTotal: (total) => `${total} row(s)`,
            onChange: (p, ps) => { setCurrent(p); setPageSize(ps); },
          }}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
            columnWidth: 24,
            preserveSelectedRowKeys: true,
          }}
        />
      </Space>
    </Modal>
  );
};

export default PendingWeldsSelectModal;
